// ============================================================================
// CONTACTOS (C4) — la libreta del negocio, con importación desde Excel/CSV.
//
// La importación es la parte delicada y por eso NO importa a ciegas: primero
// lee el archivo, muestra QUÉ va a pasar (cuántos entran, cuántos ya existen,
// cuáles se van a ignorar y por qué) y solo entonces deja confirmar. Una
// importación que "ya se hizo" y salió mal no se puede deshacer.
//
// La advertencia de consentimiento no es adorno legal: por política de Meta,
// tener el número NO autoriza a escribir primero. Si el negocio importa 500
// contactos creyendo que ya puede mandarles promociones, se gana un bloqueo del
// número. Por eso se dice antes, no después.
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Search, Upload, Users } from "lucide-react";
import { Avatar, Aviso, Boton, Chip, SkeletonLista, Vacio } from "./piezas.tsx";
import { explicarError, importarContactos, listarContactos, type ResumenImport } from "./datos.ts";
import { leerCsv, type FilaCsv } from "./csv.ts";
import { normalizarTelefono } from "./telefono.ts";
import { fechaCorta, nombreMostrado, type Contacto, type ContextoPanel } from "./tipos.ts";

interface Preparada {
  linea: number;
  nombre: string | null;
  waId: string;
  telefono: string;
  etiquetas: string[];
}
interface Rechazada { linea: number; valor: string; motivo: string }

/** Separa lo que se puede importar de lo que no, y quita repetidos del propio archivo. */
export function prepararFilas(filas: FilaCsv[]): { listas: Preparada[]; rechazadas: Rechazada[] } {
  const listas: Preparada[] = [];
  const rechazadas: Rechazada[] = [];
  const vistos = new Set<string>();

  for (const f of filas) {
    const t = normalizarTelefono(f.telefono);
    if (!t.ok || !t.waId) {
      rechazadas.push({ linea: f.linea, valor: f.telefono || "(vacío)", motivo: t.motivo ?? "inválido" });
      continue;
    }
    // Repetido DENTRO del archivo: mandarlo dos veces no rompe nada (la base hace
    // upsert), pero el conteo que se le muestra al usuario mentiría.
    if (vistos.has(t.waId)) {
      rechazadas.push({ linea: f.linea, valor: t.e164 ?? t.waId, motivo: "repetido en el archivo" });
      continue;
    }
    vistos.add(t.waId);
    listas.push({
      linea: f.linea, nombre: f.nombre || null, waId: t.waId,
      telefono: t.e164 ?? "", etiquetas: f.etiquetas,
    });
  }
  return { listas, rechazadas };
}

function Importador({
  ctx, onListo, onCerrar,
}: { ctx: ContextoPanel; onListo: () => void; onCerrar: () => void }) {
  const [nombreArchivo, setNombreArchivo] = useState<string | null>(null);
  const [listas, setListas] = useState<Preparada[]>([]);
  const [rechazadas, setRechazadas] = useState<Rechazada[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [resumen, setResumen] = useState<ResumenImport | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function elegir(archivo: File) {
    setError(null); setResumen(null);
    setNombreArchivo(archivo.name);
    const texto = await archivo.text();
    const lectura = leerCsv(texto);
    if (lectura.error) {
      setError(lectura.error); setListas([]); setRechazadas([]);
      return;
    }
    const { listas: l, rechazadas: r } = prepararFilas(lectura.filas);
    setListas(l); setRechazadas(r);
    if (l.length === 0 && r.length === 0) setError("El archivo no tiene filas de datos.");
  }

  async function confirmar() {
    setSubiendo(true);
    try {
      const r = await importarContactos(ctx.sb, ctx.tenantId, listas.map((f) => ({
        wa_id: f.waId, telefono: f.telefono || null, nombre: f.nombre, etiquetas: f.etiquetas,
      })));
      setResumen(r);
      onListo();
    } catch (e) {
      setError(explicarError(e));
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <div className="border border-borde rounded-xl p-4 bg-superficie space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-[15px] font-medium">Importar desde Excel o CSV</h2>
        <button onClick={onCerrar} className="ml-auto text-[13px] text-tinta-2 hover:text-tinta cursor-pointer">
          Cerrar
        </button>
      </div>

      {!resumen && (
        <>
          <p className="text-[13px] text-tinta-2">
            El archivo necesita una columna de teléfono. Si trae <em>nombre</em>, <em>etiquetas</em> o{" "}
            <em>notas</em>, también se usan.
          </p>

          <input ref={inputRef} type="file" accept=".csv,text/csv,text/plain"
            aria-label="Archivo de contactos"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void elegir(f); }}
            className="block w-full text-[13px] file:mr-3 file:py-2 file:px-3 file:rounded-lg
              file:border file:border-borde file:bg-elevado file:text-tinta file:cursor-pointer
              file:text-[13px] cursor-pointer" />

          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}

          {nombreArchivo && (listas.length > 0 || rechazadas.length > 0) && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Chip tono="marca">{listas.length} se van a importar</Chip>
                {rechazadas.length > 0 && <Chip tono="alerta">{rechazadas.length} se ignoran</Chip>}
              </div>

              {listas.length > 0 && (
                <div className="border border-borde-suave rounded-lg overflow-hidden">
                  <table className="w-full text-[13px]">
                    <thead className="bg-tenue text-tinta-2">
                      <tr>
                        <th className="text-left font-medium px-2.5 py-1.5">Nombre</th>
                        <th className="text-left font-medium px-2.5 py-1.5">Teléfono</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listas.slice(0, 5).map((f) => (
                        <tr key={f.waId} className="border-t border-borde-suave">
                          <td className="px-2.5 py-1.5 truncate">{f.nombre ?? <span className="text-tinta-2">sin nombre</span>}</td>
                          <td className="px-2.5 py-1.5 cifra">{f.telefono}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {listas.length > 5 && (
                    <p className="text-[12px] text-tinta-2 px-2.5 py-1.5 border-t border-borde-suave">
                      y {listas.length - 5} más…
                    </p>
                  )}
                </div>
              )}

              {rechazadas.length > 0 && (
                <details className="text-[13px]">
                  <summary className="cursor-pointer text-tinta-2 hover:text-tinta">
                    Ver por qué se ignoran {rechazadas.length}
                  </summary>
                  <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto scroll-fino">
                    {rechazadas.map((r, i) => (
                      <li key={`${r.linea}-${i}`} className="text-tinta-2">
                        <span className="cifra">línea {r.linea}</span> · {r.valor} — {r.motivo}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <Aviso tono="alerta">
                Tener el número no autoriza a escribirle primero: WhatsApp exige que el cliente haya
                dado su permiso. Entran como <strong>sin consentimiento</strong> y podrás responderles
                cuando ellos escriban.
              </Aviso>

              <Boton tono="marca" onClick={confirmar} disabled={subiendo || listas.length === 0}>
                <Upload size={15} /> {subiendo ? "Importando…" : `Importar ${listas.length}`}
              </Boton>
            </div>
          )}
        </>
      )}

      {resumen && (
        <div className="space-y-2">
          <Aviso tono="ok">
            Listo: <strong>{resumen.creados}</strong> nuevos
            {resumen.actualizados > 0 && <> · {resumen.actualizados} ya existían y se completaron</>}
            {resumen.ignorados > 0 && <> · {resumen.ignorados} ignorados</>}.
          </Aviso>
          <Boton onClick={() => { setResumen(null); setNombreArchivo(null); setListas([]); setRechazadas([]); }}>
            Importar otro archivo
          </Boton>
        </div>
      )}
    </div>
  );
}

export function Contactos({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId } = ctx;
  const [lista, setLista] = useState<Contacto[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [importando, setImportando] = useState(false);

  const recargar = useCallback(async () => {
    try {
      setLista(await listarContactos(sb, tenantId));
      setError(null);
    } catch (e) {
      setError(explicarError(e));
    } finally {
      setCargando(false);
    }
  }, [sb, tenantId]);

  useEffect(() => { void recargar(); }, [recargar]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return lista;
    return lista.filter((c) =>
      (c.nombre ?? "").toLowerCase().includes(q) ||
      (c.telefono_e164 ?? "").includes(q) ||
      c.wa_id.includes(q));
  }, [lista, busqueda]);

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-[19px] font-semibold tracking-tight">Contactos</h1>
          <Chip tono="neutro"><Users size={11} /> {lista.length}</Chip>
          <Boton tono="marca" onClick={() => setImportando((v) => !v)}
            className="sm:ml-auto h-8 text-[13px]">
            <Upload size={14} /> Importar
          </Boton>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[820px] mx-auto w-full space-y-4">
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}

          {importando && (
            <Importador ctx={ctx} onCerrar={() => setImportando(false)}
              onListo={() => { void recargar(); }} />
          )}

          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-tinta-2" />
            <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre o número" aria-label="Buscar contacto"
              className="w-full h-10 pl-9 pr-3 rounded-xl bg-elevado border border-borde text-[14px]
                placeholder:text-tinta-2/70 focus:outline-none focus:border-marca/50" />
          </div>

          {cargando && <SkeletonLista filas={4} />}

          {!cargando && filtrados.length === 0 && (
            <Vacio titulo={busqueda ? "Sin resultados" : "Todavía no hay contactos"}>
              {busqueda
                ? "Prueba con otro nombre o número."
                : "Se crean solos cuando alguien escribe por WhatsApp, o los importas desde tu Excel."}
            </Vacio>
          )}

          {filtrados.length > 0 && (
            <ul className="border border-borde rounded-xl overflow-hidden bg-superficie">
              {filtrados.map((c) => (
                <li key={c.id} className="flex items-center gap-3 px-3 py-2.5 border-b border-borde-suave
                  last:border-b-0">
                  <Avatar nombre={nombreMostrado(c)} size={36} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px]">{nombreMostrado(c)}</span>
                    <span className="block text-[12.5px] text-tinta-2 cifra">
                      {c.telefono_e164 ?? c.wa_id}
                    </span>
                  </span>
                  <span className="hidden sm:flex items-center gap-1.5">
                    {c.etiquetas.slice(0, 2).map((t) => <Chip key={t} tono="neutro">{t}</Chip>)}
                    {c.opt_in_estado !== "opt_in" && (
                      <Chip tono="alerta" titulo="No ha dado permiso para escribirle primero">
                        <AlertTriangle size={10} /> sin permiso
                      </Chip>
                    )}
                  </span>
                  <span className="text-[11.5px] text-tinta-2 cifra shrink-0">
                    {fechaCorta(c.creado_en)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
